// 通用 hooks：加载数据（含重载）、动作执行（忙碌态 + 错误）
import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import { errorText } from "./api";

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (d: T | null | ((prev: T | null) => T | null)) => void;
}

export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const gen = useRef(0);

  const reload = useCallback(async () => {
    const my = ++gen.current;
    try {
      const d = await fnRef.current();
      if (my === gen.current) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (my === gen.current) setError(errorText(e));
    } finally {
      if (my === gen.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading, reload, setData };
}

/** 执行一个动作：busy 期间按钮禁用，出错把文案放到 error */
export function useAction() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async <R,>(name: string, fn: () => Promise<R>): Promise<R | undefined> => {
    setBusy(name);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(errorText(e));
      return undefined;
    } finally {
      setBusy(null);
    }
  }, []);
  return { busy, error, setError, run };
}

/** 定时器：每 ms 执行一次（组件卸载自动清理） */
export function useInterval(fn: () => void, ms: number | null): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ms === null) return;
    const t = setInterval(() => ref.current(), ms);
    return () => clearInterval(t);
  }, [ms]);
}
